/**
 * HistoricalTrendsTab — side-by-side trailing-N-period table.
 *
 * Each column is one period (W/M/Q/H/Y/D depending on global alignment),
 * each row is one metric. Search is collapsible: collapsed shows just
 * Search Total; expanded splits into P1 / P2+ / Total.
 *
 * Reuses scale/format from reportGenerator and currency filtering rules
 * from SPBReportContent.
 */

import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import type { DashboardFilters } from "../../types";
import {
  determineScale,
  convertScaleToSuffix,
  formatNumber,
  getMetricsOrder,
} from "../../utils/reportGenerator";
import {
  useHistoricalTrendsData,
  type PeriodMetrics,
} from "./useHistoricalTrendsData";
import type { ReportData, TableData } from "../../utils/reportGenerator";
import {
  exportHistoricalTrendsToCsv,
  type TrendsCsvRow,
} from "../../utils/csvExport";

type TrendView = "actuals" | "changes" | "bridge" | "growth";

const VIEW_TABS: Array<{ value: TrendView; label: string }> = [
  { value: "actuals", label: "Business Metrics" },
  { value: "changes", label: "Metric Changes" },
  { value: "bridge", label: "Revenue Bridge" },
  { value: "growth", label: "Growth Bridge" },
];

/**
 * Sub-metric labels that should be visually indented under their parent
 * row (mirrors the same set used by the main dashboard tabs).
 */
const INDENTED_LABELS = new Set([
  "Coverage",
  "CPC",
  "CPC USD",
  "CPC LOC",
  "Ad Count",
  "Ad CTR",
  "Video Revenue USD",
  "Static Revenue USD",
  "Video Revenue LOC",
  "Static Revenue LOC",
]);

function isIndentedRow(label: string): boolean {
  // Strip optional scale suffix like " (MM)" / " (B)" before lookup.
  const base = label.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return INDENTED_LABELS.has(base);
}

// Metric keys whose values get scaled (and so deserve a "(MM)"-style
// suffix on the row label rather than every cell).
const SCALED_METRIC_KEYS = new Set([
  "revenue_usd",
  "revenue_loc",
  "total_page_requests",
  "traffic",
  "video_revenue_usd",
  "static_revenue_usd",
  "video_revenue_loc",
  "static_revenue_loc",
]);

const USD_ROW_LABELS = new Set([
  "Revenue USD",
  "Video Revenue USD",
  "Static Revenue USD",
  "RPOM USD",
  "CPC USD",
]);

const LOC_ROW_LABELS = new Set([
  "Revenue LOC",
  "Video Revenue LOC",
  "Static Revenue LOC",
  "RPOM LOC",
  "CPC LOC",
]);

/** Column metric keys exposed by getMetricsOrder. */
type MetricKey = string;

interface RowDef {
  metricKey: MetricKey;
  label: string;
}

/**
 * Format a single cell value. The scale suffix (MM/B/K) is intentionally
 * dropped here; we surface it once on the row label instead so each cell
 * stays readable.
 */
/** Map MetricKey → numeric value from a PeriodMetrics. Used for P1 / P2+
 *  rows in the Business Metrics and Metric Changes views, where the rolled-up
 *  Lambda-provided values (`p1_revenue_usd`, `p1_coverage`, etc.) are the
 *  correct source — placement cells in the main `metricsTable` /
 *  `changesTable` aren't summable for rate-style metrics. */
function pluck(m: PeriodMetrics, key: MetricKey): number | null {
  switch (key) {
    case "revenue_usd":
      return m.revenue_usd;
    case "revenue_loc":
      return m.revenue_loc;
    case "total_page_requests":
    case "traffic":
      return m.total_page_requests ?? null;
    case "rpom":
      return m.rpom;
    case "rpom_loc":
      return m.rpom_loc;
    case "coverage":
      return m.coverage;
    case "cpc":
      return m.cpc;
    case "cpc_loc":
      return m.cpc_loc;
    case "ad_count":
      return m.ad_count;
    case "ad_ctr":
      return m.ad_ctr;
    case "video_revenue_usd":
      return m.video_revenue_usd ?? null;
    case "static_revenue_usd":
      return m.static_revenue_usd ?? null;
    case "video_revenue_loc":
      return m.video_revenue_loc ?? null;
    case "static_revenue_loc":
      return m.static_revenue_loc ?? null;
    default:
      return null;
  }
}

function fmtCell(
  value: number | null,
  metricKey: MetricKey,
  scale: number
): string {
  if (value == null) return "—";
  const useScale = SCALED_METRIC_KEYS.has(metricKey);
  const [body] = formatNumber(value, metricKey, useScale ? scale : undefined);
  return body;
}

/** Format a signed scaled value (used by bridge cells). */
function fmtSignedScaled(
  value: number | null,
  metricKey: MetricKey,
  scale: number
): string {
  if (value == null) return "—";
  const formatted = fmtCell(Math.abs(value), metricKey, scale);
  const stripped = formatted.replace(/^\$/, "");
  const usesDollar = formatted.startsWith("$");
  const sign = value >= 0 ? "+" : "-";
  return usesDollar ? `${sign}$${stripped}` : `${sign}${stripped}`;
}

type CellSign = "positive" | "negative" | "neutral";
interface ViewCell {
  value: string;
  sign: CellSign;
}

function signOf(n: number | null | undefined): CellSign {
  if (n == null || n === 0) return "neutral";
  return n > 0 ? "positive" : "negative";
}

type Segment =
  | "headline"
  | "search-total"
  | "search-p1"
  | "search-p2"
  | "detail"
  | "homepage";

/**
 * Cell-index ranges within a `ReportData` table row, by product. Mirrors the
 * dimOrder definition in reportGenerator.ts. A range `[from, to]` is summed
 * (inclusive of `from`, exclusive of `to`); a single index `n` is taken
 * directly. Hidden dim entries (Page1 / Page2 rollups) are NOT in cells,
 * so for P1 / P2+ we sum the visible placement cells instead.
 */
interface SegmentCols {
  /** Single column index (Total Search / Detail / Homepage). */
  index?: number;
  /** Range of placement columns to sum (used for P1 / P2+). */
  range?: [number, number];
}

const SEGMENT_COLS_SP: Record<Segment, SegmentCols> = {
  // 0: S+D+H Total
  headline: { index: 0 },
  "search-total": { index: 1 },
  // 2..6 = P1 placements (TOP, MOP, BOP, spt_inline, spt_btf)
  "search-p1": { range: [2, 7] },
  // 7..11 = P2 placements (TOP, MOP, BOP, spt_inline, spt_btf)
  "search-p2": { range: [7, 12] },
  detail: { index: 12 },
  homepage: { index: 18 },
};

const SEGMENT_COLS_SB: Record<Segment, SegmentCols> = {
  // 0: S+D Total, 1: Total Search, 2: Total Sparkle
  headline: { index: 0 },
  "search-total": { index: 1 },
  // 3..6 = P1 placements (sparkle, inline, bottom, 3psl)
  "search-p1": { range: [3, 7] },
  // 7..10 = P2 placements
  "search-p2": { range: [7, 11] },
  detail: { index: 11 },
  homepage: { index: -1 }, // SB has no Homepage
};

function colsForProduct(product: "SP" | "SB"): Record<Segment, SegmentCols> {
  return product === "SP" ? SEGMENT_COLS_SP : SEGMENT_COLS_SB;
}

/**
 * Map metricKey (from the trends row list) to the row label used in the main
 * dashboard's `metricsTable` / `bridgeTable` etc. Lookup by row label is
 * how we find the right row inside a TableData.
 */
const METRIC_KEY_TO_LABEL_BASE: Record<string, string> = {
  revenue_usd: "Revenue USD",
  revenue_loc: "Revenue LOC",
  total_page_requests: "Page Views",
  traffic: "Page Views",
  rpom: "RPOM USD",
  rpom_loc: "RPOM LOC",
  coverage: "Coverage",
  cpc: "CPC USD",
  cpc_loc: "CPC LOC",
  ad_count: "Ad Count",
  ad_ctr: "Ad CTR",
  video_revenue_usd: "Video Revenue USD",
  static_revenue_usd: "Static Revenue USD",
  video_revenue_loc: "Video Revenue LOC",
  static_revenue_loc: "Static Revenue LOC",
};

/** Find the row in a TableData whose label (with optional " (MM)" / " (B)"
 *  suffix stripped) matches the metric key's base label. */
function findRow(
  table: TableData | undefined,
  metricKey: MetricKey
): { value: string; rawValue: number | null; cssClass: string }[] | null {
  if (!table) return null;
  const base = METRIC_KEY_TO_LABEL_BASE[metricKey];
  if (!base) return null;
  const row = table.rows.find((r) => {
    const lbl = r.label.replace(/\s*\([^)]*\)\s*$/, "").trim();
    return lbl === base;
  });
  return row ? row.cells : null;
}

/** Extract a cell (or sum a range of cells) from a ReportData table for a
 *  given segment + metric. Returns `{ value, sign, raw }` so the caller can
 *  decide whether to apply color (and detect "neutral" actuals). */
function extractFromTable(
  table: TableData | undefined,
  segment: Segment,
  product: "SP" | "SB",
  metricKey: MetricKey
): { formatted: string | null; raw: number | null } {
  const cells = findRow(table, metricKey);
  if (!cells) return { formatted: null, raw: null };
  const cols = colsForProduct(product)[segment];
  if (cols.index != null) {
    if (cols.index < 0 || cols.index >= cells.length)
      return { formatted: null, raw: null };
    const c = cells[cols.index];
    return { formatted: c.value, raw: c.rawValue };
  }
  if (cols.range) {
    const [from, to] = cols.range;
    let sum = 0;
    let any = false;
    for (let i = from; i < to && i < cells.length; i++) {
      const v = cells[i].rawValue;
      if (v != null) {
        sum += v;
        any = true;
      }
    }
    if (!any) return { formatted: null, raw: null };
    return { formatted: null, raw: sum };
  }
  return { formatted: null, raw: null };
}

/** Pick the correct table from a ReportData based on the selected sub-tab. */
function tableForView(
  reportData: ReportData,
  view: TrendView
): TableData | undefined {
  switch (view) {
    case "actuals":
      return reportData.metricsTable;
    case "changes":
      return reportData.changesTable;
    case "bridge":
      return reportData.bridgeTable;
    case "growth":
      return reportData.growthTable;
  }
}

/** Sign-format a signed scaled $ value (Bridge view rendering). */
function fmtBridgeFromRaw(raw: number | null, scale: number): string {
  if (raw == null) return "—";
  return fmtSignedScaled(raw, "revenue_usd", scale);
}

/** Render a cell from a `ReportData` table for a given (segment, metric, view).
 *  Whenever possible reads the cell value the main dashboard already
 *  formatted; for P1 / P2+ which aren't surfaced as cells, we sum the
 *  visible placement cells. Coloring matches the main tab. */
function cellFromReport(
  view: TrendView,
  segment: Segment,
  reportData: ReportData,
  product: "SP" | "SB",
  metricKey: MetricKey,
  scale: number,
  // Rolled-up P1 / P2+ target and baseline metrics (from PeriodColumn). Only
  // consulted for the actuals / changes views on segment === "search-p1" /
  // "search-p2", where placement-cell summing produces nonsense (cells
  // contain rate-style metrics that aren't summable across placements).
  // Bridge / growth views correctly sum placement cells because those cells
  // hold additive contribution amounts.
  rolledTarget?: PeriodMetrics,
  rolledBaseline?: PeriodMetrics
): ViewCell {
  const isSearchSubRow = segment === "search-p1" || segment === "search-p2";
  const useRolled =
    isSearchSubRow && (view === "actuals" || view === "changes");

  if (useRolled && rolledTarget && rolledBaseline) {
    const tv = pluck(rolledTarget, metricKey);
    const bv = pluck(rolledBaseline, metricKey);
    if (view === "actuals") {
      return { value: fmtCell(tv, metricKey, scale), sign: "neutral" };
    }
    // changes: % change with same one-decimal format the main tab uses
    if (tv == null || bv == null || bv === 0)
      return { value: "—", sign: "neutral" };
    const pct = ((tv - bv) / bv) * 100;
    const sign = pct > 0 ? "+" : "";
    return {
      value: `${sign}${pct.toFixed(1)}%`,
      sign: signOf(pct),
    };
  }

  const table = tableForView(reportData, view);
  const { formatted, raw } = extractFromTable(
    table,
    segment,
    product,
    metricKey
  );

  // For Total cells we typically have a pre-formatted string from the main
  // tab; for P1 / P2+ on bridge/growth we have a summed raw number that IS
  // mathematically correct (placement bridge contributions are additive).
  //
  // Scaled metrics (Revenue, Page Views) need consistent units across
  // P1 / P2+ / Total within a single row. The main tab formats Total cells
  // with its own scale (computed from total magnitude), while P1 / P2+ use
  // the trends `sharedScale`. To keep all three columns in the same units,
  // re-format scaled rows from `raw` using `sharedScale` instead of the
  // pre-formatted main-tab string. Non-scaled rows still use the main-tab
  // string (it carries native units like %, $/click, etc.).
  const isScaled = SCALED_METRIC_KEYS.has(metricKey);

  switch (view) {
    case "actuals": {
      if (isScaled) {
        return { value: fmtCell(raw, metricKey, scale), sign: "neutral" };
      }
      if (formatted != null) return { value: formatted, sign: "neutral" };
      return { value: fmtCell(raw, metricKey, scale), sign: "neutral" };
    }

    case "changes": {
      if (formatted != null) {
        return { value: formatted, sign: signOf(raw) };
      }
      return { value: "—", sign: "neutral" };
    }

    case "bridge": {
      // Bridge values are dollar contributions — always scaled. Re-format
      // every cell (Total, P1, P2+) from raw using sharedScale so units
      // line up with the row label suffix.
      return {
        value: fmtBridgeFromRaw(raw, scale),
        sign: signOf(raw),
      };
    }

    case "growth": {
      if (formatted != null) {
        return { value: formatted, sign: signOf(raw) };
      }
      if (raw == null) return { value: "—", sign: "neutral" };
      const pct = raw * 100;
      const sign = pct > 0 ? "+" : "";
      return {
        value: `${sign}${pct.toFixed(1)}%`,
        sign: signOf(raw),
      };
    }
  }
}

interface Props {
  filters: Partial<DashboardFilters>;
  product: "SP" | "SB";
  currency: "USD" | "LOC";
  trailingCount?: number;
  /** Optional CSV exporter registration (provided by DashboardShell via
   *  SPBReportContent). Registered when this tab mounts, cleared on
   *  unmount, so the global download button targets whichever sub-tab
   *  view is currently displayed. */
  registerCsvExporter?: (
    exporter: (() => { csv: string; filename: string }) | null
  ) => void;
}

// Per-alignment caps to bound the number of parallel Athena queries.
const TRAILING_COUNT_BY_ALIGNMENT: Record<string, number> = {
  D: 14,
  W: 10,
  M: 12,
  Q: 5,
  H: 4,
  Y: 3,
};

const HistoricalTrendsTab: React.FC<Props> = ({
  filters,
  product,
  currency,
  trailingCount,
  registerCsvExporter,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [view, setView] = useState<TrendView>("actuals");

  const effectiveTrailingCount =
    trailingCount ??
    TRAILING_COUNT_BY_ALIGNMENT[(filters.alignment as string) ?? "W"] ??
    9;

  const { periods, reports, isLoading, error } = useHistoricalTrendsData(
    filters,
    effectiveTrailingCount
  );

  const rows: RowDef[] = useMemo(() => {
    const all = getMetricsOrder(product).map(([key, label]) => ({
      metricKey: key,
      label,
    }));
    const hidden = currency === "USD" ? LOC_ROW_LABELS : USD_ROW_LABELS;
    return all.filter((r) => {
      const base = r.label.replace(/\s*\([^)]*\)\s*$/, "").trim();
      return !hidden.has(base);
    });
  }, [product, currency]);

  // Compute a single shared scale for revenue rows so columns are comparable.
  const sharedScale = useMemo(() => {
    let max = 0;
    for (const p of periods) {
      const v = p.searchTotal.revenue_usd;
      if (v != null && Math.abs(v) > max) max = Math.abs(v);
    }
    return determineScale(max || 1);
  }, [periods]);

  const showHomepage = product === "SP";

  // Register a tab-aware CSV exporter so the global download button writes
  // the currently-selected sub-tab view (Business Metrics / Metric Changes /
  // Revenue Bridge / Growth Bridge), respects currency, and includes
  // P1 / P2+ rows when Search is expanded.
  React.useEffect(() => {
    if (!registerCsvExporter) return;
    if (periods.length === 0 || reports.length === 0) {
      registerCsvExporter(null);
      return;
    }

    const buildCsv = () => {
      const periodLabels = periods.map((p) => p.label);
      const csvRows: TrendsCsvRow[] = [];
      const headlineLabel = showHomepage
        ? "Search & Detail & Homepage"
        : "Search & Detail";
      const categories: Array<{
        key: "headline" | "search" | "detail" | "homepage";
        label: string;
      }> = [
        { key: "headline", label: headlineLabel },
        { key: "search", label: "Search" },
        { key: "detail", label: "Detail" },
        ...(showHomepage
          ? [{ key: "homepage" as const, label: "Homepage" }]
          : []),
      ];

      const showScaleSuffix = view === "actuals" || view === "bridge";
      const scaleSuffixLocal = convertScaleToSuffix(sharedScale);

      for (const cat of categories) {
        const isSearch = cat.key === "search";
        for (const r of rows) {
          const isScaledRow = SCALED_METRIC_KEYS.has(r.metricKey);
          const labelWithUnit =
            isScaledRow && scaleSuffixLocal && showScaleSuffix
              ? `${r.label} (${scaleSuffixLocal})`
              : r.label;

          const segments: Array<{
            seg: Segment;
            tag: "Total" | "P1" | "P2+";
          }> = [];
          if (cat.key === "headline") {
            segments.push({ seg: "headline", tag: "Total" });
          } else if (isSearch && searchExpanded) {
            segments.push({ seg: "search-p1", tag: "P1" });
            segments.push({ seg: "search-p2", tag: "P2+" });
            segments.push({ seg: "search-total", tag: "Total" });
          } else if (isSearch) {
            segments.push({ seg: "search-total", tag: "Total" });
          } else if (cat.key === "detail") {
            segments.push({ seg: "detail", tag: "Total" });
          } else {
            segments.push({ seg: "homepage", tag: "Total" });
          }

          for (const { seg, tag } of segments) {
            const values = periods.map((p, pi) => {
              const periodReport = reports[pi];
              if (!periodReport) return "—";
              const rolledTarget =
                seg === "search-p1"
                  ? p.searchP1
                  : seg === "search-p2"
                  ? p.searchP2
                  : undefined;
              const rolledBaseline =
                seg === "search-p1"
                  ? p.baselineSearchP1
                  : seg === "search-p2"
                  ? p.baselineSearchP2
                  : undefined;
              const cell = cellFromReport(
                view,
                seg,
                periodReport.reportData,
                product,
                r.metricKey,
                sharedScale,
                rolledTarget,
                rolledBaseline
              );
              return cell.value;
            });
            csvRows.push({
              category: cat.label,
              metric: labelWithUnit,
              segment: tag,
              values,
            });
          }
        }
      }

      const viewLabel =
        VIEW_TABS.find((t) => t.value === view)?.label ?? "Business Metrics";
      const alignmentLabel = (filters.alignment as string) ?? "W";
      const comparisonModeLabel = (filters.comparisonMode as string) ?? "YoY";
      const geoLabel = String(filters.geo ?? "ww").toUpperCase();
      const deviceLabel = String(filters.device ?? "Desktop+Mobile").replace(
        "-",
        "+"
      );
      const csv = exportHistoricalTrendsToCsv(
        viewLabel,
        product,
        geoLabel,
        deviceLabel,
        currency,
        alignmentLabel,
        comparisonModeLabel,
        periodLabels,
        csvRows
      );
      const tagSafe = viewLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const filename = `finn-historical-trends-${product.toLowerCase()}-${tagSafe}.csv`;
      return { csv, filename };
    };

    registerCsvExporter(buildCsv);
    return () => {
      registerCsvExporter(null);
    };
  }, [
    registerCsvExporter,
    periods,
    reports,
    rows,
    view,
    searchExpanded,
    sharedScale,
    showHomepage,
    product,
    currency,
    filters.alignment,
    filters.comparisonMode,
    filters.geo,
    filters.device,
  ]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1.5 }}>
        {error}
      </Alert>
    );
  }

  if (isLoading && periods.length === 0) {
    const periodsLabel = effectiveTrailingCount;
    const periodWord = periodWordFor(filters.alignment as string | undefined);
    return (
      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 8,
        }}
      >
        <CircularProgress size={32} thickness={4} />
        <Typography variant="body2" color="text.secondary">
          Loading {periodsLabel} {periodWord} of historical data…
        </Typography>
      </Box>
    );
  }

  if (!isLoading && periods.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1.5 }}>
        Apply filters to load historical trends.
      </Alert>
    );
  }

  // Style tokens — kept local to the component to mirror ReportTable.
  const hdrDark = isDark ? "#1a3a6a" : "#1a5aa0";
  const hdrText = "#ffffff";
  const rowLabelBg = isDark ? "#151e2d" : "#f8f9fa";
  const cellBorder = isDark ? "rgba(255,255,255,0.08)" : "#dee2e6";
  const stripeBg = isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.02)";
  const hoverBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(44,120,212,0.05)";

  const scaleSuffix = convertScaleToSuffix(sharedScale);

  return (
    <Box sx={{ mt: 1.5 }}>
      {isLoading && periods.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            px: 1.25,
            py: 0.75,
            borderRadius: 1,
            bgcolor: isDark ? "rgba(33,150,243,0.08)" : "rgba(33,150,243,0.06)",
            border: `1px solid ${
              isDark ? "rgba(33,150,243,0.25)" : "rgba(33,150,243,0.2)"
            }`,
          }}
        >
          <CircularProgress size={14} thickness={5} />
          <Typography variant="caption" color="text.secondary">
            Refreshing historical data…
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress sx={{ height: 3, borderRadius: 1 }} />
          </Box>
        </Box>
      )}
      <Tabs
        value={view}
        onChange={(_, v) => setView(v as TrendView)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          mb: 1.5,
          "& .MuiTab-root": {
            textTransform: "none",
            minHeight: 36,
            fontWeight: 600,
            fontSize: "0.8rem",
          },
        }}
      >
        {VIEW_TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.25,
          fontSize: "0.85rem",
          color: theme.palette.text.secondary,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Trailing {periods.length} {periodWordFor(filters.alignment)} ending{" "}
          {periods[periods.length - 1]?.label ?? ""}
        </Typography>
      </Box>
      <Box sx={{ overflowX: "auto", borderRadius: 1 }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            fontSize: "0.78rem",
            fontFamily: theme.typography.fontFamily,
          }}
        >
          <thead>
            {/* Single header row: Metric | W/E period 1 | W/E period 2 | ... */}
            <tr>
              <th
                style={{
                  ...thStyle(hdrDark, hdrText, cellBorder),
                  minWidth: 200,
                  textAlign: "left",
                  paddingLeft: 14,
                }}
              >
                Metric
              </th>
              {periods.map((p, i) => (
                <th
                  key={`p-${i}`}
                  style={thStyle(hdrDark, hdrText, cellBorder)}
                >
                  {p.label}
                </th>
              ))}
            </tr>
          </thead>
          {/* Body is structured as 2 (SP) or 3 (SB) category groups; each
              category emits one bold "category header" row plus one row per
              metric. For Search, when expanded, each metric also gets P1 and
              P2+ sub-rows. */}
          <tbody>
            {(
              [
                {
                  key: "headline",
                  label: showHomepage
                    ? "Search & Detail & Homepage"
                    : "Search & Detail",
                } as const,
                { key: "search", label: "Search" } as const,
                { key: "detail", label: "Detail" } as const,
                ...(showHomepage
                  ? [{ key: "homepage", label: "Homepage" } as const]
                  : []),
              ] as Array<{
                key: "headline" | "search" | "detail" | "homepage";
                label: string;
              }>
            ).map((cat) => {
              const isSearch = cat.key === "search";
              const numPeriodCols = periods.length;
              return (
                <React.Fragment key={cat.key}>
                  {/* Category header row */}
                  <tr>
                    <td
                      colSpan={1 + numPeriodCols}
                      style={{
                        padding: "8px 14px",
                        backgroundColor: rowLabelBg,
                        border: `1px solid ${cellBorder}`,
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        color: theme.palette.text.primary,
                        position: "sticky",
                        left: 0,
                      }}
                    >
                      {cat.label}
                      {isSearch && (
                        <Tooltip
                          title={
                            searchExpanded
                              ? "Collapse P1 / P2+"
                              : "Expand to P1 / P2+"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => setSearchExpanded((v) => !v)}
                            sx={{
                              p: 0,
                              ml: 1,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {searchExpanded ? (
                              <KeyboardArrowDownIcon fontSize="inherit" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="inherit" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                  {/* Metric rows */}
                  {rows.map((r, ri) => {
                    const showScaleSuffix =
                      view === "actuals" || view === "bridge";
                    const isScaled = SCALED_METRIC_KEYS.has(r.metricKey);
                    const labelWithUnit =
                      isScaled && scaleSuffix && showScaleSuffix
                        ? `${r.label} (${scaleSuffix})`
                        : r.label;
                    const bg = ri % 2 === 0 ? "transparent" : stripeBg;

                    const renderMetricRow = (
                      subKey: "total" | "p1" | "p2",
                      subLabel: string,
                      indent: number,
                      bold = false
                    ) => (
                      <tr
                        key={`${cat.key}-${r.metricKey}-${subKey}`}
                        style={{ background: bg }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = hoverBg)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = bg)
                        }
                      >
                        <td
                          style={{
                            padding: "6px 14px",
                            paddingLeft: indent,
                            backgroundColor: rowLabelBg,
                            border: `1px solid ${cellBorder}`,
                            whiteSpace: "nowrap",
                            fontWeight: bold ? 700 : 500,
                            color: theme.palette.text.primary,
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                          }}
                        >
                          {subLabel}
                        </td>
                        {periods.map((p, pi) => {
                          // Map (category, sub-row) to the column-segment used
                          // for ReportData cell extraction. P1/P2+ only apply
                          // to the Search category; Detail/Homepage have no
                          // sub-row breakdown.
                          let segment: Segment = "search-total";
                          if (cat.key === "headline") {
                            segment = "headline";
                          } else if (cat.key === "search") {
                            segment =
                              subKey === "p1"
                                ? "search-p1"
                                : subKey === "p2"
                                ? "search-p2"
                                : "search-total";
                          } else if (cat.key === "detail") {
                            segment = "detail";
                          } else {
                            segment = "homepage";
                          }

                          const periodReport = reports[pi];
                          // Rolled-up P1 / P2+ target+baseline metrics from
                          // the Lambda. Used for actuals / changes views on
                          // Search P1 / P2+ rows (placement-cell summing
                          // would yield wrong rates).
                          const rolledTarget =
                            subKey === "p1"
                              ? p.searchP1
                              : subKey === "p2"
                              ? p.searchP2
                              : undefined;
                          const rolledBaseline =
                            subKey === "p1"
                              ? p.baselineSearchP1
                              : subKey === "p2"
                              ? p.baselineSearchP2
                              : undefined;
                          let cell: ViewCell;
                          if (periodReport) {
                            cell = cellFromReport(
                              view,
                              segment,
                              periodReport.reportData,
                              product,
                              r.metricKey,
                              sharedScale,
                              rolledTarget,
                              rolledBaseline
                            );
                          } else {
                            cell = { value: "—", sign: "neutral" };
                          }
                          return renderCell(
                            cell.value,
                            `${pi}`,
                            cellBorder,
                            bold,
                            cell.sign
                          );
                        })}
                      </tr>
                    );

                    // Sub-metric rows (Coverage, CPC, Ad Count, Ad CTR) are
                    // visually indented under their parent metric, mirroring
                    // the main dashboard tabs.
                    const subIndent = isIndentedRow(labelWithUnit) ? 16 : 0;
                    if (isSearch && searchExpanded) {
                      return (
                        <React.Fragment key={`${cat.key}-${r.metricKey}`}>
                          {renderMetricRow(
                            "p1",
                            `${labelWithUnit} (P1)`,
                            32 + subIndent
                          )}
                          {renderMetricRow(
                            "p2",
                            `${labelWithUnit} (P2+)`,
                            32 + subIndent
                          )}
                          {renderMetricRow(
                            "total",
                            `${labelWithUnit} (Total)`,
                            32 + subIndent,
                            true
                          )}
                        </React.Fragment>
                      );
                    }
                    return renderMetricRow(
                      "total",
                      labelWithUnit,
                      24 + subIndent
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

function thStyle(
  bg: string,
  color: string,
  border: string
): React.CSSProperties {
  return {
    padding: "8px 10px",
    textAlign: "center",
    fontWeight: 600,
    fontSize: "0.72rem",
    whiteSpace: "nowrap",
    backgroundColor: bg,
    color,
    border: `1px solid ${border}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
  };
}

function renderCell(
  value: string,
  key: string,
  border: string,
  emphasize = false,
  sign: CellSign = "neutral"
): React.ReactNode {
  // Match the main dashboard tabs: green for positive, red for negative,
  // theme default for neutral / actuals view.
  const color =
    sign === "positive"
      ? "#28a745"
      : sign === "negative"
      ? "#dc3545"
      : undefined;
  return (
    <td
      key={key}
      style={{
        padding: "6px 10px",
        textAlign: "right",
        border: `1px solid ${border}`,
        fontVariantNumeric: "tabular-nums",
        fontWeight: emphasize ? 600 : 400,
        color,
      }}
    >
      {value}
    </td>
  );
}

function periodWordFor(alignment: string | undefined): string {
  switch (alignment) {
    case "D":
      return "days";
    case "W":
      return "weeks";
    case "M":
      return "months";
    case "Q":
      return "quarters";
    case "H":
      return "halves";
    case "Y":
      return "years";
    default:
      return "periods";
  }
}

export default HistoricalTrendsTab;
